import React, { useState, useEffect } from 'react';
import axios from 'axios';
import UserManagement from './admin/UserManagement';
import PendingUsers from './admin/PendingUsers';
import CategoryPermissions from './admin/CategoryPermissions';
import CommandManagement from './admin/CommandManagement';
import AdminNavigation from './admin/AdminNavigation';
import PanelSettings from './admin/PanelSettings';
import './admin/AdminPanel.css';

const API_BASE = process.env.REACT_APP_API_BASE; // Direct backend URL when no dev proxy is used

function AdminPanel({ userData, refreshPermissions, initialTab = 'dashboard' }) {
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [myPermissions, setMyPermissions] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [permissionGroups, setPermissionGroups] = useState({});
    const [pendingUsersCount, setPendingUsersCount] = useState(0);


    // 2. Fix für loadPermissions (ca. Zeile 22)
    const loadPermissions = async () => {
        try {

            const allSystemPermissions = [
                'view_users', 'manage_users', 'ban_users', 'manage_support',
                'team_view', 'manage_meetings', 'manage_team_members', 'view_whitelist_stats', 'manage_absences', 'view_team_management',
                'manage_permissions', 'admin_access', 'manage_system', 'view_admin_panel', 'view_logs', 'manage_roles', 'view_analysis', 'view_faction_overview', 'manage_commands', 'manage_streamers',
                'view_extra_modules', 'manage_giveaways', 'manage_polls', 'manage_automod', 'view_user_check'
            ];

            // Group permissions
            const groups = {
                "Support Area": allSystemPermissions.filter(p => ['view_users', 'manage_users', 'ban_users', 'manage_support'].includes(p)),
                "Team Leitung": allSystemPermissions.filter(p => ['team_view', 'manage_meetings', 'manage_team_members', 'view_whitelist_stats', 'manage_absences', 'view_team_management'].includes(p)),
                "Admin": allSystemPermissions.filter(p => ['manage_permissions', 'admin_access', 'manage_system', 'view_admin_panel', 'view_logs', 'manage_roles'].includes(p)),
                "Andere": allSystemPermissions.filter(p => !['view_users', 'manage_users', 'ban_users', 'manage_support', 'team_view', 'manage_meetings', 'manage_team_members', 'view_whitelist_stats', 'manage_absences', 'view_team_management', 'manage_permissions', 'admin_access', 'manage_system', 'view_admin_panel', 'view_logs', 'manage_roles', 'view_analysis', 'view_extra_modules', 'manage_giveaways', 'manage_polls', 'manage_automod', 'view_user_check'].includes(p)),
                "Analyse": allSystemPermissions.filter(p => ['view_analysis'].includes(p)),
                "Fraktionsverwaltung": allSystemPermissions.filter(p => ['view_faction_overview'].includes(p)),
                "Streaming": allSystemPermissions.filter(p => ['manage_streamers'].includes(p)),
                "Zusatz Module": allSystemPermissions.filter(p => ['view_extra_modules', 'manage_giveaways', 'manage_polls', 'manage_automod', 'view_user_check'].includes(p)),

            };
            setPermissionGroups(groups);
        } catch (err) {
            console.error('Error grouping permissions:', err);
        }
    };

    const loadData = async () => {
        const token = localStorage.getItem('discord_token');
        if (!token) return;
        try {
            setLoading(true);
            const [rolesResp, myPermsResp, myRolesResp, usersResp] = await Promise.all([
                axios.get(`${API_BASE}/api/roles`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_BASE}/api/my-permissions`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_BASE}/api/my-roles`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_BASE}/api/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
            ]);
            setRoles(rolesResp.data);
            setMyPermissions(myPermsResp.data);
            setUserRoles(myRolesResp.data);
            setUsers(usersResp.data);

            // Load pending users count for Panel Admins
            try {
                const pendingResp = await axios.get(`${API_BASE}/api/pending-users`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setPendingUsersCount(pendingResp.data.length);
            } catch (e) {
                // Not a Panel Admin, ignore
                setPendingUsersCount(0);
            }

            setError(null);
        } catch (err) {
            setError('Fehler beim Laden der Daten.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); loadPermissions(); }, []);

    const hasPermission = (pName) => {
        // Super-Admin Check
        if (userData.id === process.env.REACT_APP_SUPER_ADMIN_USER) return true;

        // Check gegen das neue Array-Format ['perm1', 'perm2']
        if (!myPermissions || !Array.isArray(myPermissions)) return false;
        return myPermissions.includes(pName);
    };

    // --- NEU: FUNKTIONEN FÜR USER MANAGEMENT ---

    const handleAssignRole = async (userId, roleId) => {
        try {
            const token = localStorage.getItem('discord_token');
            await axios.post(`${API_BASE}/api/users/${userId}/roles`, { roleId }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            await loadData(); // Liste aktualisieren
            if (userId === userData.id && refreshPermissions) refreshPermissions();
            return true;
        } catch (err) {
            alert('Fehler beim Zuweisen der Rolle');
            return false;
        }
    };

    const handleRemoveRole = async (userId, roleId) => {
        try {
            const token = localStorage.getItem('discord_token');
            // 1. Anfrage an das Backend
            await axios.delete(`${API_BASE}/api/users/${userId}/roles/${roleId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // 2. WICHTIG: Warte kurz und lade dann die Daten KOMPLETT neu
            // Das stellt sicher, dass inherited_permissions und Rollen-Badges synchron sind
            await loadData();

            console.log("Rolle wurde entfernt und Daten neu geladen");
        } catch (err) {
            alert('Fehler beim Entfernen der Rolle: ' + err.message);
        }
    };
    const handleDeleteUser = async (userId) => {
        if (!window.confirm("Benutzer wirklich aus dem System löschen?")) return;
        try {
            const token = localStorage.getItem('discord_token');
            console.log(`[DELETE] Attempting to delete user ${userId} from ${API_BASE}/api/users/${userId}`);
            const response = await axios.delete(`${API_BASE}/api/users/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('[DELETE] Response:', response);
            await loadData();
        } catch (err) {
            console.error('[DELETE] Error:', err);
            console.error('[DELETE] Error response:', err.response);
            alert('Fehler beim Löschen des Benutzers: ' + (err.response?.data?.error || err.message));
        }
    };


    const hasAnyAccess = myPermissions.length > 0 || userRoles.length > 0 || userData?.id === process.env.REACT_APP_SUPER_ADMIN_USER;

    const renderContent = () => {
        // Wenn der Nutzer keine Berechtigungen hat -> Hinweis anzeigen
        if (!hasAnyAccess) {
            return (
                <div className="card no-permissions">
                    <h4>Keine Berechtigungen</h4>
                    <p className="muted">Sie haben aktuell keine Rechte für das Admin Panel. Bitte wenden Sie sich an einen Admin, um Berechtigungen zu erhalten.</p>
                </div>
            );
        }

        const errorAlert = error ? (
            <div className="card card-error">
                <p>⚠️ {error}</p>
            </div>
        ) : null;

        switch (activeTab) {
            case 'pending-users':
                return (
                    <>
                        {errorAlert}
                        <PendingUsers onUserApproved={() => {
                            setPendingUsersCount(prev => Math.max(0, prev - 1));
                            loadData();
                        }} />
                    </>
                );
            case 'users':
                return (
                    <>
                        {errorAlert}
                        <UserManagement
                            users={users}
                            roles={roles}
                            permissionGroups={permissionGroups}
                            hasPermission={hasPermission}
                            onAssignRole={handleAssignRole}   // JETZT ÜBERGEBEN
                            onRemoveRole={handleRemoveRole}   // JETZT ÜBERGEBEN
                            onDeleteUser={handleDeleteUser}   // JETZT ÜBERGEBEN
                            refreshUsers={loadData}           // Refresh function für Permissions-Update
                        />
                    </>
                );
            case 'category-permissions':
                return (
                    <>
                        {errorAlert}
                        <CategoryPermissions hasPermission={hasPermission} permissionGroups={permissionGroups} refreshPermissions={refreshPermissions} refreshAdminData={loadData} />
                    </>
                );
            case 'commands':
                return (
                    <>
                        {errorAlert}
                        <CommandManagement />
                    </>
                );
            case 'panel-settings':
                return (
                    <>
                        {errorAlert}
                        <PanelSettings hasPermission={hasPermission} />
                    </>
                );
            default:
                return null;
        }
    };

    if (loading) return (
        <div className="admin-panel-container">
            <div className="dashboard-main admin-panel-main">
                <div className="admin-header">
                    <h1>Administration</h1>
                    <p className="muted">Lädt...</p>
                </div>
                <div className="loading-container">
                    <div className="spinner"></div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="admin-panel-container">
            <div className="dashboard-main admin-panel-main">
                <div className="admin-header">
                    <h1>Administration</h1>
                    <p className="muted">Willkommen zurück, {userData.displayname}.</p>
                </div>
                <AdminNavigation
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    hasPermission={hasPermission}
                    userRoles={userRoles}
                    pendingUsersCount={pendingUsersCount}
                />
                {renderContent()}
            </div>
        </div>
    );
}

export default AdminPanel;